import type { QueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import type { DagTableLoadResponse } from '@/types/dag'

export interface DagRowCacheUpdate {
  cells: Record<string, unknown>
  newVersion?: number
}

/**
 * Updates a single row's cells (and optionally its version) in all matching
 * DAG table query caches.
 */
export function updateDagRowInCache(
  queryClient: QueryClient,
  planId: string,
  versionId: string,
  rowId: string,
  cellUpdates: Record<string, unknown>,
  newVersion?: number
) {
  updateDagRowsInCache(
    queryClient,
    planId,
    versionId,
    new Map([[rowId, { cells: cellUpdates, newVersion }]])
  )
}

/**
 * Applies several row updates in one cache write, so a burst of SSE events
 * produces one re-render instead of one per event. Rows not in `updates`
 * keep their object identity. A row's version never moves backwards, so a
 * late-flushed SSE update can't undo a newer version from a PATCH response.
 */
export function updateDagRowsInCache(
  queryClient: QueryClient,
  planId: string,
  versionId: string,
  updates: Map<string, DagRowCacheUpdate>
) {
  if (updates.size === 0) return
  queryClient.setQueriesData<DagTableLoadResponse>(
    { queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId) },
    old => {
      if (!old) return old
      return {
        ...old,
        rows: old.rows.map(row => {
          let next = row
          const own = updates.get(row.id)
          if (own) {
            const bumpVersion =
              own.newVersion !== undefined &&
              own.newVersion > (row.row_version ?? 0)
            next = {
              ...next,
              ...(bumpVersion && { row_version: own.newVersion }),
              cells: { ...next.cells, ...own.cells },
            }
          }
          const parentId = row.parent_line_item?.id
          const parentUpdate = parentId ? updates.get(parentId) : undefined
          if (parentUpdate && next.parent_line_item) {
            const { cells } = parentUpdate
            if ('plan_sku_num' in cells || 'description' in cells) {
              next = {
                ...next,
                parent_line_item: {
                  ...next.parent_line_item,
                  ...('plan_sku_num' in cells && {
                    sku_number: cells.plan_sku_num as string | null,
                  }),
                  ...('description' in cells && {
                    description: cells.description as string | null,
                  }),
                },
              }
            }
          }
          return next
        }),
      }
    }
  )
}
